/**
 * `queryPersist` — local persistence for the `@tanstack/query` cache (Phase 3 perf,
 * 2026-06-10).
 *
 * Without this, the in-memory query cache is wiped on every page reload, so a cold start
 * (or a refresh in the Tauri webview) re-fetches the notebook tree + every recently-opened
 * note from the network before anything renders. Persisting the cache to IndexedDB lets a
 * reload paint the last-seen notes + tree INSTANTLY from disk, then revalidate in the
 * background once the stale window passes.
 *
 * Design choices:
 *  - **IndexedDB, not localStorage.** Note bodies are larger than localStorage's ~5MB
 *    synchronous budget; IndexedDB is async with a far larger quota. Backed by idb-keyval.
 *  - **Whitelist what persists.** Only durable corpus reads — single notes, the notebook
 *    tree, note titles, tags/entities — are dehydrated. Live/polling data (health metrics,
 *    extraction queue) and one-off searches are excluded so a reload never restores stale
 *    dashboards or a stranger's search.
 *  - **Version buster = build sha.** The cache key is busted by `__APP_VERSION__`, so a
 *    frontend deploy discards the old persisted cache (schema/shape changes can't leak
 *    across versions). Backend-only changes are covered by the normal stale revalidation.
 *  - **maxAge 24h.** Anything older than a day is dropped on restore rather than shown.
 */

import { get as idbGet, set as idbSet, del as idbDel } from 'idb-keyval';
import { createAsyncStoragePersister } from '@tanstack/query-async-storage-persister';
import type { PersistQueryClientOptions } from '@tanstack/react-query-persist-client';
import type { Query } from '@tanstack/react-query';

/** The IndexedDB key the dehydrated cache is stored under. */
const PERSIST_KEY = 'aletheia.rq-cache.v1';

/** Query-key roots that are SAFE + USEFUL to persist across reloads. */
const PERSIST_PREFIXES: ReadonlyArray<string> = [
  'note', // single note bodies (the editor + sidecar)
  'note-titles', // autocomplete + tree titles
  'notebook-children', // the notebook tree levels
  'notebook-notes', // notes within an expanded notebook
  'tags', // tag lists
  'entities', // entity lists
  'backlinks', // a note's backlinks
];

/** The build sha injected by Vite (see vite.config.ts `define`), used as the cache buster. */
declare const __APP_VERSION__: string;

/**
 * Drop the entire persisted query cache from IndexedDB.
 *
 * Call this right before a `window.location.reload()` that must reflect a server-side write
 * the in-memory cache doesn't know about (e.g. `restore_note_revision`, which writes a note
 * body out-of-band from the editor's save path). Without it, the reload rehydrates the stale
 * pre-write body — the `note` key is on {@link PERSIST_PREFIXES} and note bodies carry a 5-min
 * `staleTime`, so the rehydrated copy is treated as fresh and never refetched. Clearing the
 * key forces the reload to cold-fetch from the server. Reload is imminent, so re-warming the
 * cache from the network once is a non-issue.
 */
export async function clearPersistedQueryCache(): Promise<void> {
  await idbDel(PERSIST_KEY);
}

/**
 * Build the `persistOptions` for `<PersistQueryClientProvider>`. Returns the IndexedDB
 * persister plus the dehydrate whitelist + version buster.
 */
export function buildPersistOptions(): Omit<PersistQueryClientOptions, 'queryClient'> {
  const persister = createAsyncStoragePersister({
    storage: {
      getItem: (key) => idbGet(key).then((v) => v ?? null),
      setItem: (key, value) => idbSet(key, value),
      removeItem: (key) => idbDel(key),
    },
    key: PERSIST_KEY,
    // Don't thrash IndexedDB on every cache mutation; batch writes.
    throttleTime: 1000,
  });

  return {
    persister,
    // 24h: older entries are discarded on restore rather than shown stale.
    maxAge: 24 * 60 * 60 * 1000,
    // Bust the whole persisted cache when the frontend build changes.
    buster: typeof __APP_VERSION__ === 'string' ? __APP_VERSION__ : 'dev',
    dehydrateOptions: {
      shouldDehydrateQuery: (query: Query) => {
        // Only persist successful queries whose key root is in the whitelist.
        if (query.state.status !== 'success') return false;
        const root = Array.isArray(query.queryKey) ? query.queryKey[0] : query.queryKey;
        return typeof root === 'string' && PERSIST_PREFIXES.includes(root);
      },
    },
  };
}
